/**
 * FieldViewPanel (Unit 5 Phase 1) — the View sheet: the records screen's one
 * entry point to Filter, Sort and Group.
 *
 * Bob's hamburger panel, rebuilt as a bottom sheet. His prototype hung these
 * three off a ☰ in the tab row (toolsight-inv-v11_11.html:3975-3990); this zone
 * has no header button cluster at all — one back button and a footer — so the
 * three verbs hang off a fourth footer slot instead. Same three destinations,
 * same "active" indicator idea (his updateHpanelButtons), native chrome.
 *
 * SHELL MECHANICS are a deliberate SIBLING copy of FieldSyncSheet's, which is
 * itself a sibling of BottomSheetSelect's D-5.3-8 contract — not an extraction
 * (both live in paths this phase does not own):
 *   - backdrop tap        → onClose
 *   - drag-down > 80px    → onClose
 *   - Escape              → capture-phase + stopPropagation, so a ModalShell
 *                           underneath never also closes
 *   - hardware/gesture back → pops the host's sentinel; this unmounts
 *
 * NO isOpen PROP. The host MOUNT-GATES on tmgFieldViewPanelOpen, the sentinel
 * 6/7 style (FieldZonePlaceholder:505/:520). Mount IS open, so the effects
 * below take no isOpen dependency and there is no second source of truth for
 * whether the sheet is up.
 *
 * "NO HANDLER, NO CONTROL" — FieldRecordsFooter.jsx:9-15, applied verbatim. A
 * row renders only when its handler prop is supplied. PHASE 1 PASSES onSort
 * ONLY: Filter and Group do not render at all until their phases wire them.
 * Deliberately not disabled rows — a greyed control on a phone reads as broken,
 * invites a tap that does nothing, and still costs its height.
 *
 * The ACTIVE INDICATOR is a count, not a dot: "3 levels" tells a rep both that
 * a sort is on and how much of one, which is the question they actually have
 * when they open this sheet to find out why the grid looks the way it does.
 */

import React, { useEffect, useRef, useState } from 'react';
import './FieldViewPanel.css';

const DRAG_CLOSE_PX = 80;

/* Icons: 24-box, currentColor, stroke-width 2, round caps/joins — the field
   zone's established inline-SVG convention (FieldSyncChip.jsx:31-65,
   FieldRecordsFooter.jsx:30-31). */

function FilterIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
  );
}

function SortIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="3" y1="6" x2="15" y2="6" />
      <line x1="3" y1="12" x2="11" y2="12" />
      <line x1="3" y1="18" x2="7" y2="18" />
      <polyline points="17 9 20 6 17 3" />
      <line x1="20" y1="6" x2="20" y2="20" />
    </svg>
  );
}

function GroupIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="3" width="18" height="6" rx="1" />
      <rect x="3" y="13" width="18" height="3" rx="1" />
      <rect x="3" y="18" width="18" height="3" rx="1" />
    </svg>
  );
}

function ChevronIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

/** "1 level" / "3 levels" — plural handled once, at the only site that needs it. */
function levelLabel(count) {
  return `${count} level${count === 1 ? '' : 's'}`;
}

export default function FieldViewPanel({
  onFilter, onSort, onGroup, onClose,
  filterCount = 0, sortCount = 0, groupCount = 0,
  escapeEnabled = true,
}) {
  const [dragY, setDragY] = useState(0);
  const dragStartY = useRef(null);

  // Escape (desktop) — capture + stopPropagation: close THIS sheet only.
  // Mirrors FieldSyncSheet.jsx:205-215 / BottomSheetSelect.jsx:50-60.
  //
  // ⚠ escapeEnabled EXISTS BECAUSE THIS PANEL STAYS MOUNTED UNDERNEATH.
  // Sort and Filter push their sentinels ON TOP of this one, so this component
  // is still mounted — and still listening — while an editor covers it. Two
  // capture-phase listeners on `window` BOTH fire: stopPropagation does not
  // stop a sibling listener on the same target in the same phase (only
  // stopImmediatePropagation would), and this one is registered first because
  // it mounted first. Left ungated, Escape inside the Filter modal's value
  // picker would close the picker AND pop this panel's navigate(-1) in one
  // keypress, discarding the rep's whole draft.
  //
  // So the rule is: the panel owns Escape only while it is the TOPMOST layer.
  // Each editor owns Escape for itself when it is up. The host supplies the
  // predicate because only the host knows which sentinels are set.
  useEffect(() => {
    if (!escapeEnabled) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [escapeEnabled, onClose]);

  const handleDragStart = (e) => {
    dragStartY.current = e.touches[0].clientY;
  };
  const handleDragMove = (e) => {
    if (dragStartY.current == null) return;
    setDragY(Math.max(0, e.touches[0].clientY - dragStartY.current));
  };
  const handleDragEnd = () => {
    if (dragStartY.current == null) return;
    const shouldClose = dragY > DRAG_CLOSE_PX;
    dragStartY.current = null;
    setDragY(0);
    if (shouldClose) onClose();
  };

  // Built as a list rather than three inline blocks, for the same reason
  // FieldRecordsFooter builds `slots`: the separators must fall BETWEEN
  // rendered rows only. These rows stack vertically, so the rule is a
  // :not(:last-child) border in the stylesheet rather than the interleaved
  // element the footer needs for a horizontal bar — but the failure being
  // avoided is identical, a rule stranded under a row that is not there.
  const rows = [];
  if (onFilter) {
    rows.push({
      id: 'filter', label: 'Filter', testId: 'field-view-filter',
      onClick: onFilter, Icon: FilterIcon, count: filterCount,
      summary: filterCount > 0 ? `${filterCount} active` : 'None',
    });
  }
  if (onSort) {
    rows.push({
      id: 'sort', label: 'Sort', testId: 'field-view-sort',
      onClick: onSort, Icon: SortIcon, count: sortCount,
      summary: sortCount > 0 ? levelLabel(sortCount) : 'None',
    });
  }
  if (onGroup) {
    rows.push({
      id: 'group', label: 'Group', testId: 'field-view-group',
      onClick: onGroup, Icon: GroupIcon, count: groupCount,
      summary: groupCount > 0 ? `${groupCount} active` : 'None',
    });
  }

  return (
    <div data-testid="field-view-panel">
      <div className="fvp-backdrop" data-testid="fvp-backdrop" onClick={onClose} />
      <div
        className="fvp-sheet"
        role="dialog"
        aria-modal="true"
        aria-label="View"
        style={dragY > 0 ? { transform: `translateY(${dragY}px)`, animation: 'none' } : undefined}
      >
        <div
          className="fvp-grab"
          onTouchStart={handleDragStart}
          onTouchMove={handleDragMove}
          onTouchEnd={handleDragEnd}
          onTouchCancel={handleDragEnd}
        >
          <div className="fvp-handle" aria-hidden="true" />
          <div className="fvp-title">View</div>
        </div>
        <div className="fvp-body">
          {rows.map(({ id, label, testId, onClick, Icon, count, summary }) => (
            <button
              key={id}
              type="button"
              className={`fvp-row${count > 0 ? ' fvp-row-active' : ''}`}
              data-testid={testId}
              onClick={onClick}
            >
              <span className="fvp-row-icon"><Icon /></span>
              <span className="fvp-row-label">{label}</span>
              {/* Bob's updateHpanelButtons idea: the row itself carries the
                  state, so a rep can see WITHOUT opening anything that a
                  sort is on. The dot is redundant with the summary text and
                  that is intentional — colour alone is never the only
                  carrier of a state. */}
              {count > 0 && <span className="fvp-row-dot" aria-hidden="true" />}
              <span className="fvp-row-summary">{summary}</span>
              <span className="fvp-row-chevron"><ChevronIcon /></span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
